/**
 * Tonearm — the arm of the deck, with its needle in the groove of the second playing, and a
 * headshell a finger can lift and put down elsewhere.
 *
 * Task 20. `spec.md` «Пластинка», «Тонарм»: the needle at the record's outer edge is 0:00, at
 * the edge of the label the end of the track, in a straight line between (`lib/tonearm.ts`).
 * The second is the slice's `positionS` — the element's, or, while a hand plays the sound
 * elsewhere, the scratch voice's (`PlayerController.showPosition`, task 18): under a scratch the
 * arm follows the voice, not the element that waits at speed 0.
 *
 * The drawing is one picture of the arm at rest (`assets/tonearm.svg`), turned whole about the
 * pivot by the stylesheet (`--arm`, `styles/deck.css`); only the angle is React's. That is a few
 * renders a second of one small component while a track plays — the slider's own pace — and
 * none at all under a still finger on the record, whose sound does not move.
 *
 * ## The needle in a hand (task 22)
 *
 * The headshell takes a finger. Lifted, the needle leaves the groove: the element goes to speed
 * 0 without a pause, as under a finger on the record (`engine/speed.ts`), and the record turns
 * on underneath. Carried, the arm turns about its pivot as far as the finger does (the short way
 * round, so a finger taken past the pivot does not flip it), within the first and the last
 * groove it may be put down in; the second under the needle is shown on the slider and is what
 * turns the arm, through the same `showPosition`. Put down, the element is sent to that second
 * and sounds at once, without the record's slope (`speed.releaseNow`).
 *
 * Every other way out — the system cancels the touch, the capture is lost, the screen closes,
 * «уменьшение движения» comes on — puts the needle back where it was lifted: the track goes on
 * from there. All of them give back what the lift took: the element's speed, the deck
 * (`store/deck.ts`, one hand at a time — the record is not held while the needle is), and
 * Telegram's vertical swipes. No needle is lifted under «уменьшение движения» or for a track of
 * unknown length: the arm only shows where the track is.
 */

import type { CSSProperties, JSX, PointerEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';

import tonearmUrl from '../assets/tonearm.svg';
import { player } from '../engine/player';
import { scratch } from '../engine/scratch';
import { angleAt, shortestArc } from '../lib/platter';
import type { Point } from '../lib/platter';
import { telegram } from '../lib/telegram';
import { DECK_MM, PIVOT, armTurnFor, carryRange, grooveAt, needleTime } from '../lib/tonearm';
import type { RecordSize } from '../lib/tonearm';
import { leaveDeck, takeDeck } from '../store/deck';
import { usePlayer } from '../store/player';

import { useReducedMotion } from './motion';

export interface TonearmProps {
  /** Which record the needle is on: each has grooves of its own. */
  readonly size: RecordSize;
}

/** A needle in a hand. */
interface Lift {
  readonly pointerId: number;
  /** The pivot on the screen: the finger's angle is measured about it. */
  readonly pivot: Point;
  readonly releaseSwipes: () => void;
  /** Where the finger was last, in degrees about the pivot. */
  fingerDeg: number;
  /** How far the arm is turned now, in degrees from its rest. */
  turn: number;
  /** The second under the needle now. */
  atS: number;
}

export function Tonearm({ size }: TonearmProps): JSX.Element {
  const positionS = usePlayer((s) => s.positionS);
  const durationS = usePlayer((s) => s.durationS);
  const reducedMotion = useReducedMotion();
  const [lifted, setLifted] = useState(false);
  const lift = useRef<Lift | undefined>(undefined);
  const turn = armTurnFor(grooveAt(positionS, durationS, size));
  const style = { '--arm': `${turn.toFixed(3)}deg` } as CSSProperties;

  /**
   * Gives back what the lift took. `put` — the needle goes down where it is; otherwise it goes
   * back to where it was lifted. `undefined` when no needle is in a hand, so a second way out
   * finds nothing to do.
   */
  const settle = useCallback((put: boolean): Lift | undefined => {
    const current = lift.current;
    if (current === undefined) return undefined;
    lift.current = undefined;
    // The swipes first: whatever else fails on the way out, the hold must not outlive the touch.
    current.releaseSwipes();
    const controls = player();
    if (put) controls.seek(current.atS);
    controls.showPosition(undefined);
    controls.speed.releaseNow();
    leaveDeck('arm');
    setLifted(false);
    return current;
  }, []);

  // Closed under a finger, or «уменьшение движения» switched on under it: the needle goes back.
  useEffect(
    () => () => {
      settle(false);
    },
    [settle],
  );
  useEffect(() => {
    if (reducedMotion) settle(false);
  }, [reducedMotion, settle]);

  function onPointerDown(event: PointerEvent<HTMLDivElement>): void {
    if (lift.current !== undefined || event.isPrimary === false || event.button !== 0) return;
    // No handlers at all for a track of unknown length (below): its grooves have no seconds.
    const state = usePlayer.getState();
    const deck = event.currentTarget.closest('.deck')?.getBoundingClientRect();
    if (deck === undefined || deck.width <= 0 || deck.height <= 0) return;
    // One hand at a time: a finger on the record keeps the needle where it is.
    if (!takeDeck('arm')) return;
    event.stopPropagation();
    const pivot = {
      x: deck.left + (deck.width * PIVOT.x) / DECK_MM.width,
      y: deck.top + (deck.height * (PIVOT.y + DECK_MM.hinges)) / DECK_MM.height,
    };
    // A scratch still handing its sound back stops here: the needle has the record now.
    scratch().abort();
    const controls = player();
    controls.speed.hush();
    lift.current = {
      pointerId: event.pointerId,
      pivot,
      releaseSwipes: telegram.holdVerticalSwipes(),
      fingerDeg: angleAt({ x: event.clientX, y: event.clientY }, pivot),
      turn: armTurnFor(grooveAt(state.positionS, state.durationS, size)),
      atS: state.positionS,
    };
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // A webview without pointer capture: the needle still follows while the finger stays on.
    }
    controls.showPosition(state.positionS);
    setLifted(true);
  }

  function onPointerMove(event: PointerEvent<HTMLDivElement>): void {
    const current = lift.current;
    if (current?.pointerId !== event.pointerId) return;
    const fingerDeg = angleAt({ x: event.clientX, y: event.clientY }, current.pivot);
    const step = shortestArc(fingerDeg - current.fingerDeg);
    current.fingerDeg = fingerDeg;
    if (step === 0) return;
    const state = usePlayer.getState();
    const { from, to } = carryRange(state.durationS, state.seekLimitS, size);
    current.turn = Math.min(to, Math.max(from, current.turn + step));
    current.atS = needleTime(current.turn, state.durationS, state.seekLimitS, size);
    player().showPosition(current.atS);
  }

  function onPointerUp(event: PointerEvent<HTMLDivElement>): void {
    if (lift.current?.pointerId !== event.pointerId) return;
    settle(true);
  }

  function onPointerAbandoned(event: PointerEvent<HTMLDivElement>): void {
    if (lift.current?.pointerId !== event.pointerId) return;
    settle(false);
  }

  const handlers =
    reducedMotion || !(durationS > 0)
      ? {}
      : {
          onPointerDown,
          onPointerMove,
          onPointerUp,
          onPointerCancel: onPointerAbandoned,
          onLostPointerCapture: onPointerAbandoned,
        };

  return (
    <div
      className="deckPart deckArm"
      style={style}
      data-turn={turn.toFixed(3)}
      data-lifted={lifted ? 'true' : 'false'}
      aria-hidden="true"
    >
      <img className="deckArmArt" src={tonearmUrl} alt="" draggable={false} />
      <div className="deckPart deckHeadshell" {...handlers} />
    </div>
  );
}
